import {forwardRef, useEffect, useRef, useState} from "react";
import {TreeItem} from "@mui/x-tree-view";
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView";
import {DragIndicator, Remove, Edit, ArrowUpward, ArrowDownward} from "@mui/icons-material";
import {IconButton, Modal, Box, Typography, Autocomplete} from "@mui/material";
import CustomTextField from "./CustomTextField.tsx";
import {DefaultButton} from "./Buttons.tsx";

const generateId = () => Math.random().toString(36).slice(2, 9);


const DropZone = ({onDrop}) => {
    const [active, setActive] = useState(false);
    return (
        <div
            style={{
                height: "10px",
                backgroundColor: active ? "var(--primary-colorSwitched)" : "var(--primary-color)",
            }}
            onDragOver={(e) => {
                e.preventDefault();
                setActive(true);
            }}
            onDragLeave={() => setActive(false)}
            onDrop={(e) => {
                e.preventDefault();
                setActive(false);
                onDrop();
            }}
        />
    );
};

interface TreeItemNode {
    id: string;
    label: string;
    children?: TreeItemNode[];
}

interface AutocompleteTextInputTreeProps {
    items: TreeItemNode[];
    suggestions: string[];
    setItems: React.Dispatch<React.SetStateAction<TreeItemNode[]>>;
    headline: string;
    textfieldLabel: string;
}


const AutocompleteTextInputTree = forwardRef<HTMLInputElement, AutocompleteTextInputTreeProps>(({
                                                                                                    items,
                                                                                                    setItems,
                                                                                                    suggestions,
                                                                                                    headline,
                                                                                                    textfieldLabel
                                                                                                }, ref) => {
    const [expandedItems, setExpandedItems] = useState([]);

    const [draggingItem, setDraggingItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editItem, setEditItem] = useState(null);
    const [editText, setEditText] = useState("");
    const [inputText, setInputText] = useState("");
    const [inputTextError, setInputTextError] = useState(false);
    const [inputTextTouched, setInputTextTouched] = useState(false);

    const inputRef = useRef(null);

    // Sync the forwarded ref with internal ref
    useEffect(() => {
        if (typeof ref === 'function') {          // i.e. callback ref (ref is a function)
            ref(inputRef.current);
        } else if (ref) {                         // i.e. ref created with useRef()
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
        }
    }, [ref]);

    // UseRef and UseEffect hook that set the selected Item to null if the person clicks outside of the list
    const treeContainerRef = useRef(null);
    useEffect(() => {
      const handleMouseDownOutside = (event) => {
        const clickedElement = event.target;

        const clickedInsideTree = treeContainerRef.current?.contains(clickedElement);
        const clickedAutocompleteOption = clickedElement.closest('.MuiAutocomplete-popper, .MuiAutocomplete-option');

        if (!clickedInsideTree && !clickedAutocompleteOption) {
          setSelectedItem(null);
        }
      };

      document.addEventListener("mousedown", handleMouseDownOutside);
      return () => {
        document.removeEventListener("mousedown", handleMouseDownOutside);
      };
    }, []);

    // If Parent changes the items, expand all items
    const internalUpdateRef = useRef(false);
    const prevItemsRef = useRef<TreeItemNode[]>([]);
    useEffect(() => {
    const prev = JSON.stringify(prevItemsRef.current);
    const next = JSON.stringify(items);

    if (prev !== next) {
      if (internalUpdateRef.current) {
      // internal add/edit — don’t auto‑expand
      internalUpdateRef.current = false;
      } else {
        // external change — expand all
        const getAllIds = (nodes: TreeItemNode[]): string[] =>
          nodes.reduce<string[]>((acc, n) =>
              acc.concat(n.id, n.children ? getAllIds(n.children) : []),
          []);

        setExpandedItems(getAllIds(items));
      }
      prevItemsRef.current = items;
    }
}, [items]);

    // Methods concerning the Item/ Text that is to be added to the tree
    useEffect(() => {
        setInputTextError(inputTextTouched && !inputText);
    }, [inputText, inputTextTouched]);
    const addItem = () => {
        if (inputText) {
          internalUpdateRef.current = true;
          const newItem = {id: generateId(), label: inputText, children: []};
          // setItems([...items, newItem]);
          setItems((prevItems) => {
              if (selectedItem) {
                  // Add to selected item's children
                  const addToSelected = (nodes) =>
                      nodes.map((n) => {
                          if (n.id === selectedItem.id) {
                              setExpandedItems((prevExpanded) => [...new Set([...prevExpanded, n.id])]); // Add parent to the expanded items list
                              return {...n, children: [...(n.children || []), newItem]};
                          }
                          if (n.children) return {...n, children: addToSelected(n.children)};
                          return n;
                      });

                  return addToSelected(prevItems);
              } else {
                  // Add to root
                  return [...prevItems, newItem];
              }
          });
          setSelectedItem(null);
          setInputText("");
          setInputTextTouched(false);
        } else {
          setInputTextTouched(true);
        }
    };

    // Methods concerning Drag and Drop functionality
    const handleDragStart = (node) => {
        setDraggingItem(node);
    };
    const removeNode = (nodes, id) => {
      return nodes
        .filter((n) => n.id !== id)
        .map((n) =>
          n.children ? {...n, children: removeNode(n.children, id)} : n
        );
    };
    const handleReorderDrop = (targetNode, position) => {
        const insertNodeAtPosition = (nodes, targetId, nodeToInsert, position) => {
            return nodes.map((n) => {
                if (n.id === targetId) {
                    let newChildren = [...(n.children || [])];
                    newChildren.splice(position, 0, nodeToInsert); // Insert at the right position
                    return {...n, children: newChildren};
                }
                if (n.children) {
                    return {...n, children: insertNodeAtPosition(n.children, targetId, nodeToInsert, position)};
                }
                return n;
            });
        };
        if (!draggingItem) return;

        setItems((prevItems) => {
            let updatedItems = removeNode(prevItems, draggingItem.id);
            if (!targetNode) {
                updatedItems.splice(position, 0, draggingItem);
            } else {
                updatedItems = insertNodeAtPosition(updatedItems, targetNode.id, draggingItem, position);
            }
            return updatedItems;
        });
        setDraggingItem(null); // Clear drag state
    };
    const handleDropOnItem = (targetItem) => {
        if (!draggingItem || draggingItem.id === targetItem.id) return;

        setItems((prev) => {
            const removeItem = (nodes) =>
                nodes.filter((n) => n.id !== draggingItem.id).map((n) => ({
                    ...n,
                    children: n.children ? removeItem(n.children) : []
                }));

            let newList = removeItem(prev);

            targetItem.children = targetItem.children || [];
            targetItem.children.push(draggingItem);

            // Add draggingItem to the target's children
            const addItemToTarget = (nodes) =>
                nodes.map((n) => {
                    if (n.id === targetItem.id) {
                        return {...n, children: [...(n.children || []), draggingItem]};
                    }
                    if (n.children) {
                        return {...n, children: addItemToTarget(n.children)};
                    }
                    return n;
                });

            newList = addItemToTarget(newList);

            setExpandedItems((prevExpanded) => [...new Set([...prevExpanded, targetItem.id])]);
            setDraggingItem(null);
            return [...newList];
        });
    };
    // Function to move an item within the tree structure (For the up and down arrow buttons)
    const moveItem = (itemId, direction) => {
        setItems((prevItems) => {
            const findNode = (nodes, id, parent = null) => {
                for (let i = 0; i < nodes.length; i++) {
                    if (nodes[i].id === id) return {node: nodes[i], index: i, parent, nodes};
                    if (nodes[i].children) {
                        const found = findNode(nodes[i].children, id, nodes[i]);
                        if (found) return found;
                    }
                }
                return null;
            };

            const swapNodes = (arr, index1, index2) => {
                [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
            };

            let newItems = [...prevItems];
            const found = findNode(newItems, itemId);
            if (!found) return prevItems;

            const {node, index, parent, nodes} = found;

            if (direction === -1) {
                // Move up
                if (index > 0) {
                    // Swap with previous sibling
                    swapNodes(nodes, index, index - 1);
                } else if (parent) {
                    // Move above parent
                    const parentFound = findNode(newItems, parent.id);
                    if (!parentFound) return prevItems;
                    const {index: parentIndex, nodes: parentNodes} = parentFound;
                    parent.children = parent.children.filter((n) => n.id !== node.id);
                    parentNodes.splice(parentIndex, 0, node);
                }
            } else if (direction === 1) {
                // Move down
                if (index < nodes.length - 1) {
                    // Swap with next sibling
                    swapNodes(nodes, index, index + 1);
                } else if (parent) {
                    // Move below parent
                    const parentFound = findNode(newItems, parent.id);
                    if (!parentFound) return prevItems;
                    const {index: parentIndex, nodes: parentNodes} = parentFound;
                    parent.children = parent.children.filter((n) => n.id !== node.id);
                    parentNodes.splice(parentIndex + 1, 0, node);
                }
            }

            return [...newItems];
        });
    };

    // Methods concerning the Edit Button
    const handleEditClick = (item) => {
        setEditItem(item);
        setEditText(item.label);
    };

    const handleSaveEdit = () => {
        if (!editText) return;
        setItems((prevItems) => {
            const updateLabel = (nodes) =>
                nodes.map((n) => {
                    if (n.id === editItem.id) return {...n, label: editText};
                    if (n.children) return {...n, children: updateLabel(n.children)};
                    return n;
                });
            return updateLabel(prevItems);
        });
        setEditItem(null);
    };

    // Recursive renderer. The additional parameter "parent" is null at top-level.
    const renderTree = (nodes, parent = null, depth = 0) => (
        nodes.map((node, index) => (
            <div key={node.id} style={{position: "relative"}}>
                {/* Drop zone ABOVE this item if it is the first in the list*/}
                {index == 0 && <DropZone onDrop={() => handleReorderDrop(parent, index)}/>}
                <TreeItem
                    key={node.id}
                    itemId={node.id}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(node);
                    }}
                    label={
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px",
                                borderRadius: "4px",
                                justifyContent: "start"
                            }}
                            draggable
                            onDragStart={() => handleDragStart(node)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleDropOnItem(node)}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(node);
                            }}
                        >
                            <IconButton aria-label="drag"><DragIndicator/></IconButton>
                            <CustomTextField label={textfieldLabel} value={node.label}
                                             sx={{width: "500px"}} onClick={(e) => e.stopPropagation()}
                                             onKeyDown={(e) => e.stopPropagation()}
                                             onChange={(e) =>
                                                 setItems((prev) => {
                                                     // update the node label recursively
                                                     const updateLabel = (nodes) =>
                                                         nodes.map((n) => {
                                                             if (n === node) return {...n, label: e.target.value};
                                                             if (n.children) return {
                                                                 ...n,
                                                                 children: updateLabel(n.children)
                                                             };
                                                             return n;
                                                         });
                                                     return updateLabel(prev);
                                                 })
                                             }
                            />
                            <IconButton onClick={() => moveItem(node.id, -1)} disabled={parent === null && index === 0}><ArrowUpward/></IconButton>
                            <IconButton onClick={() => moveItem(node.id, 1)}
                                        disabled={parent === null && index === nodes.length - 1}><ArrowDownward/></IconButton>
                            <IconButton onClick={() => handleEditClick(node)}><Edit/></IconButton>
                          {(node.id !== "0" && node.id !== "1") &&
                              <IconButton onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(null);
                                setItems((prev) => {
                                  return removeNode(prev, node.id);
                                });
                              }} color="error">
                                  <Remove/>
                              </IconButton>
                          }
                        </div>
                    }
                    sx={{
                        backgroundColor: selectedItem?.id === node.id ? "var(--naturGreenAlpha05)" : "var(--background-main)",
                        "& .MuiTreeItem-content.Mui-selected": {backgroundColor: "transparent"},
                        "& .MuiTreeItem-content.Mui-selected:hover": {backgroundColor: "var(--primary-color)"},
                        "& .MuiTreeItem-content:hover": {backgroundColor: "var(--primary-color)"},
                    }}
                >
                    {/* Render children only if there are actual children */}
                    {node.children && node.children.length > 0 ? (
                        <div>{renderTree(node.children, node, depth + 1)}</div>
                    ) : null}
                </TreeItem>
                {/* Drop Zone BELOW the item */}
                <DropZone onDrop={() => handleReorderDrop(parent, index + 1)}/>
            </div>
        ))
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                width: "80%",
                gap: "15px",
                borderStyle: "solid",
                borderWidth: "1px",
                borderRadius: "10px",
                paddingBottom: "15px",
            }}
            ref={treeContainerRef}
        >
            <h3>{headline}</h3>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "start",
                    columnGap: "15px",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: "100%",
                    marginBottom: "15px",
                }}
            >
                <Autocomplete
                    freeSolo
                    options={suggestions}
                    value={inputText}
                    onChange={(_e, newValue) => {
                        setInputText(newValue);
                        setTimeout(() => inputRef.current?.setSelectionRange(0, 0), 0);
                    }}
                    onInputChange={(_e, newValue) => setInputText(newValue)}
                    renderInput={(params) => (
                        <CustomTextField
                            {...params}
                            value={null}
                            label={textfieldLabel}
                            is_required={true}
                            manualError={inputTextError}
                            errorText={"Dieses Feld darf nicht leer sein!"}
                            sx={{width: "650px"}}
                            onBlur={() => {
                                setInputTextTouched(true);
                            }}
                            inputRef={inputRef}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && inputText) {
                                    e.preventDefault();
                                    addItem();
                                }
                            }}
                        />
                    )}
                />
                <DefaultButton
                    label={"Hinzufügen"}
                    onClick={addItem}
                    sx={{width: "125px", minWidth: "125px", height: "55px"}}
                    variant={"contained"}
                    color={!inputText ? "red" : ""}
                />
            </div>
            <SimpleTreeView expandedItems={expandedItems || []}
                            onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
                            expansionTrigger="iconContainer">{renderTree(items)}</SimpleTreeView>
            {/* Edit Modal */}
            <Modal open={Boolean(editItem)} onClose={() => setEditItem(null)}>
                <Box sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 600,
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: "8px",
                }}>
                    <Typography variant="h6">Eintrag bearbeiten</Typography>
                    <p></p>
                    <CustomTextField
                        label={textfieldLabel}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        sx={{width: "100%", marginBottom: "16px"}}
                        manualError={!editText}
                        errorText={"Dieses Feld darf nicht leer sein!"}
                    />
                    <div style={{display: "flex", flexDirection: "row", gap: "15px", justifyContent: "center"}}>
                        <DefaultButton label={"Abbrechen"}
                                       onClick={() => setEditItem(null)}
                                       sx={{width: "125px", minWidth: "125px", height: "55px"}} color={"red"}
                                       variant={"contained"}/>
                        <DefaultButton label={"Speichern"}
                                       onClick={() => {
                                           if (editText) {
                                               handleSaveEdit();
                                           }
                                       }}
                                       sx={{width: "125px", minWidth: "125px", height: "55px"}}/>
                    </div>
                </Box>
            </Modal>
        </div>
    );
});

export default AutocompleteTextInputTree;
